import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Tag,
  Settings2,
  FileText,
  DollarSign,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  Loader2,
  Banknote,
  AlertTriangle,
  ArrowLeftRight,
  TrendingUp,
} from "lucide-react";
import { Input } from "../../ui/Input";
import { Button } from "../../ui/Button";
import { DepreciationPanel } from "./DepreciationPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { AutocompleteInput, AutocompleteOption } from "../../ui";
import { useCatalog } from "../../../hooks/useCatalog";
import { useVehicleOptions } from "../../../hooks/useVehicleOptions";
import { PriceInput } from "../../form";
import { formatCurrency, parseCurrency } from "../../../utils/formatters";
import { apiService } from "../../../services/api";
import { cn } from "../../../utils/cn";

// ── Types ──────────────────────────────────────────────────────────────────────

interface TradeVehicleExpense {
  name: string;
  value: number;
}

interface TradeVehicleData {
  brandId: string;
  modelId: string;
  versionId?: string;
  condition: string;
  modelYear: number;
  manufactureYear: number;
  mileage: number;
  fuelType: string;
  transmission: string;
  color: string;
  offeredValue: number;
  // Car-specific
  type?: string;
  doors?: string;
  steering?: string;
  // Motorcycle-specific
  motorcycleEngineType?: string;
  motorcycleCoolingType?: string;
  motorcycleStarterType?: string;
  motorcycleBrakeType?: string;
  motorcycleFeedingType?: string;
  motorcycleStyle?: string;
  motorcycleCategory?: string;
  motorcycleCylinderCapacity?: string;
  // Truck-specific
  truckBodyType?: string;
  truckCabType?: string;
  truckAxles?: string;
  truckTraction?: string;
  truckGrossVehicleWeight?: string;
  truckPayload?: string;
  // Documentation
  plate?: string;
  chassis?: string;
  renavam?: string;
  // Financial
  expenses?: TradeVehicleExpense[];
  isAlienated?: boolean;
  alienatedDebt?: number;
}

interface TradeVehicleFormProps {
  value?: TradeVehicleData | null;
  onChange: (data: TradeVehicleData | null) => void;
  onSave?: (data: TradeVehicleData) => void;
  showSaveButton?: boolean;
  /** Preço do veículo da proposta — usado para calcular saldo */
  saleVehiclePrice?: number;
}

const ELECTRIC_FUEL_TYPES = ["electric", "hybrid"];

// ── Helpers ────────────────────────────────────────────────────────────────────

const FieldLabel = ({
  children,
  required,
  htmlFor,
  hint,
}: {
  children: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  hint?: string;
}) => (
  <div className="mb-1.5">
    <label
      htmlFor={htmlFor}
      className={cn(
        "block text-xs font-semibold uppercase tracking-widest text-muted-foreground",
        required &&
          "after:content-['*'] after:ml-0.5 after:text-destructive after:font-normal"
      )}
    >
      {children}
    </label>
    {hint && <p className="text-xs text-accent mt-0.5">{hint}</p>}
  </div>
);

/** Card section — sem overflow-hidden para não clipar dropdowns */
const SectionCard = ({
  icon: Icon,
  title,
  subtitle,
  children,
  action,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <div className="rounded-xl border border-border bg-card">
    {/* Header — rounded-t-xl para clipar o bg sem overflow no container */}
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border bg-surface rounded-t-xl">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent/10 shrink-0">
          <Icon className="h-4 w-4 text-accent" />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-foreground leading-none">
            {title}
          </h4>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
      </div>
      {action}
    </div>
    {/* Body */}
    <div className="p-6">{children}</div>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────
export const TradeVehicleForm: React.FC<TradeVehicleFormProps> = ({
  value,
  onChange,
  onSave,
  showSaveButton = false,
  saleVehiclePrice,
}) => {
  const {
    brands,
    models,
    versions,
    fetchBrands,
    fetchBrandModels,
    fetchModelVersions,
    fetchVersionYears,
    versionYears,
    isLoading: catalogLoading,
  } = useCatalog();
  const { options: vehicleOptions } = useVehicleOptions();

  const [brandSearch, setBrandSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [versionSearch, setVersionSearch] = useState("");
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [fipeValue, setFipeValue] = useState<{
    value: string;
    numericValue: number;
  } | null>(null);
  const [isLoadingFipe, setIsLoadingFipe] = useState(false);
  const [showDocFields, setShowDocFields] = useState(false);
  const [category, setCategory] = useState<string>("");

  const [formData, setFormData] = useState<TradeVehicleData>({
    brandId: value?.brandId || "",
    modelId: value?.modelId || "",
    versionId: value?.versionId || "",
    condition: value?.condition || "used",
    modelYear: value?.modelYear || new Date().getFullYear(),
    manufactureYear: value?.manufactureYear || new Date().getFullYear(),
    mileage: value?.mileage || 0,
    fuelType: value?.fuelType || "",
    transmission: value?.transmission || "",
    color: value?.color || "",
    offeredValue: value?.offeredValue || 0,
    type: value?.type || "",
    doors: value?.doors || "",
    steering: value?.steering || "",
    motorcycleEngineType: value?.motorcycleEngineType || "",
    motorcycleCoolingType: value?.motorcycleCoolingType || "",
    motorcycleStarterType: value?.motorcycleStarterType || "",
    motorcycleBrakeType: value?.motorcycleBrakeType || "",
    motorcycleFeedingType: value?.motorcycleFeedingType || "",
    motorcycleStyle: value?.motorcycleStyle || "",
    motorcycleCategory: value?.motorcycleCategory || "",
    motorcycleCylinderCapacity: value?.motorcycleCylinderCapacity || "",
    truckBodyType: value?.truckBodyType || "",
    truckCabType: value?.truckCabType || "",
    truckAxles: value?.truckAxles || "",
    truckTraction: value?.truckTraction || "",
    truckGrossVehicleWeight: value?.truckGrossVehicleWeight || "",
    truckPayload: value?.truckPayload || "",
    plate: value?.plate || "",
    chassis: value?.chassis || "",
    renavam: value?.renavam || "",
    expenses: value?.expenses || [],
    isAlienated: value?.isAlienated ?? false,
    alienatedDebt: value?.alienatedDebt || 0,
  });

  const [expenseName, setExpenseName] = useState("");
  const [expenseValue, setExpenseValue] = useState("");

  const isCar = category === "B";
  const isMotorcycle = category === "A";
  const isTruck = category === "C";
  const isElectricOrHybrid = ELECTRIC_FUEL_TYPES.includes(formData.fuelType);

  // ── Side effects ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (category) fetchBrands(category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    if (formData.brandId) {
      fetchBrandModels(formData.brandId);
      if (formData.modelId) {
        const selected = models.find((m) => m.id === formData.modelId);
        if (selected && selected.brandId !== formData.brandId) {
          setFormData((p) => ({ ...p, modelId: "", versionId: "" }));
          setModelSearch("");
          setVersionSearch("");
        }
      }
    } else if (formData.modelId || formData.versionId) {
      setFormData((p) => ({ ...p, modelId: "", versionId: "" }));
      setModelSearch("");
      setVersionSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.brandId]);

  useEffect(() => {
    if (formData.modelId) {
      setIsLoadingVersions(true);
      fetchModelVersions(formData.modelId).finally(() =>
        setIsLoadingVersions(false)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.modelId]);

  useEffect(() => {
    if (formData.versionId) fetchVersionYears(formData.versionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.versionId]);

  // Auto-set transmission to automatic for electric/hybrid
  useEffect(() => {
    if (isElectricOrHybrid && formData.transmission !== "automatic") {
      setFormData((p) => ({ ...p, transmission: "automatic" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fuelType]);

  // Se a versão não tiver o ano atual disponível na FIPE, força condição "used"
  useEffect(() => {
    if (formData.versionId && versionYears.length > 0) {
      const currentYear = new Date().getFullYear();
      const hasCurrentYear = versionYears.some((vy) => vy.year.year === currentYear);
      if (!hasCurrentYear && (!formData.condition || formData.condition === "new_vehicle")) {
        setFormData((p) => ({ ...p, condition: "used_vehicle" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionYears, formData.versionId]);

  useEffect(() => {
    const fetchFipe = async () => {
      if (!formData.versionId || !formData.modelYear || versionYears.length === 0) {
        setFipeValue(null);
        return;
      }
      const selectedYearData = versionYears.find(
        (vy) => vy.year.year === formData.modelYear
      );
      if (!selectedYearData) { setFipeValue(null); return; }
      try {
        setIsLoadingFipe(true);
        const fipeData = await apiService.getFipeValue(
          formData.versionId,
          selectedYearData.yearId
        );
        setFipeValue(fipeData);
      } catch {
        setFipeValue(null);
      } finally {
        setIsLoadingFipe(false);
      }
    };
    fetchFipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.versionId, formData.modelYear, versionYears.length]);

  useEffect(() => {
    onChange(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleBrandSelect = (option: AutocompleteOption) => {
    const brand = brands.find((b) => b.id === option.id);
    if (brand) {
      setFormData((p) => ({ ...p, brandId: brand.id, modelId: "", versionId: "" }));
      setModelSearch("");
      setVersionSearch("");
    }
  };

  const handleModelSelect = (option: AutocompleteOption) => {
    const model = models.find((m) => m.id === option.id);
    if (model) {
      setFormData((p) => ({ ...p, modelId: model.id, versionId: "" }));
      setVersionSearch("");
    }
  };

const handleVersionSelect = async (option: AutocompleteOption) => {
    if (!option.id) {
      setFormData((p) => ({ ...p, versionId: "", fuelType: "", transmission: "" }));
      setVersionSearch("");
      return;
    }
    const version = versions.find((v) => v.id === option.id);
    if (version) {
      setFormData((p) => ({
        ...p,
        versionId: version.id,
        fuelType: version.fuelType || p.fuelType,
        transmission: version.transmission || p.transmission,
      }));
      setVersionSearch(version.label || version.name);
      try {
        const det = await apiService.getVersionById(version.id);
        if (det && (det as any).spec) {
          const spec = (det as any).spec;
          setFormData((p) => ({
            ...p,
            fuelType: spec.fuelType || p.fuelType,
            transmission: spec.transmission || p.transmission,
            steering: spec.steeringType || p.steering,   // API retorna steeringType
            doors: spec.doors?.toString() || p.doors,
            type: spec.type || p.type,
          }));
        }
      } catch { /* silent */ }
    }
  };

  const set = (field: keyof TradeVehicleData, val: any) =>
    setFormData((p) => ({ ...p, [field]: val }));

  const handleAddExpense = () => {
    if (expenseName && expenseValue) {
      const num = parseCurrency(expenseValue);
      if (num > 0) {
        setFormData((p) => ({
          ...p,
          expenses: [...(p.expenses || []), { name: expenseName, value: num }],
        }));
        setExpenseName("");
        setExpenseValue("");
      }
    }
  };

  const handleRemoveExpense = (idx: number) =>
    setFormData((p) => ({
      ...p,
      expenses: p.expenses?.filter((_, i) => i !== idx) || [],
    }));

  // ── Options ──────────────────────────────────────────────────────────────────
  const brandOptions: AutocompleteOption[] = brands.map((b) => ({
    id: b.id,
    label: b.name,
    image: b.logoUrl,
  }));

  const modelOptions: AutocompleteOption[] = formData.brandId
    ? models.filter((m) => m.brandId === formData.brandId).map((m) => ({ id: m.id, label: m.name }))
    : [];

  const versionOptions: AutocompleteOption[] = formData.modelId
    ? versions.filter((v) => v.modelId === formData.modelId).map((v) => ({ id: v.id, label: v.label || v.name }))
    : [];

  // Ano Modelo: usa FIPE versionYears quando disponível (com label de descontinuado)
  const modelYearOptions = versionYears.length > 0
    ? [...versionYears]
        .sort((a, b) => b.year.year - a.year.year)
        .map((vy) => ({
          value: vy.year.year.toString(),
          label: vy.year.year.toString(),
        }))
    : Array.from({ length: new Date().getFullYear() + 2 - 2000 }, (_, i) => {
        const y = 2000 + i;
        return { value: y.toString(), label: y.toString() };
      }).reverse();

  // Ano Fabricação: apenas modelYear e modelYear-1 (padrão do cadastro)
  const manufactureYearOptions = formData.modelYear
    ? [
        { value: formData.modelYear.toString(), label: formData.modelYear.toString() },
        { value: (formData.modelYear - 1).toString(), label: (formData.modelYear - 1).toString() },
      ]
    : modelYearOptions;

  const conditionOpts = vehicleOptions?.conditions?.map((c: any) => ({ value: c.value, label: c.label })) || [];
  const fuelTypeOpts   = vehicleOptions?.fuelTypes?.map((f: any) => ({ value: f.value, label: f.label })) || [];
  const transmissionOpts = vehicleOptions?.transmissions?.map((t: any) => ({ value: t.value, label: t.label })) || [];
  const colorOpts      = vehicleOptions?.colors?.map((c: any) => ({ value: c.value, label: c.label })) || [];
  const bodyTypeOpts   = vehicleOptions?.bodyTypes?.map((t: any) => ({ value: t.value, label: t.label })) || [];
  const steeringOpts   = vehicleOptions?.steeringTypes?.map((s: any) => ({ value: s.value, label: s.label })) || [];
  const motoEngineOpts   = vehicleOptions?.motorcycles?.engineTypes?.map((e: any) => ({ value: e.value, label: e.label })) || [];
  const motoCoolingOpts  = vehicleOptions?.motorcycles?.coolingTypes?.map((c: any) => ({ value: c.value, label: c.label })) || [];
  const motoStarterOpts  = vehicleOptions?.motorcycles?.starterTypes?.map((s: any) => ({ value: s.value, label: s.label })) || [];
  const motoBrakeOpts    = vehicleOptions?.motorcycles?.brakeTypes?.map((b: any) => ({ value: b.value, label: b.label })) || [];
  const motoFeedingOpts  = vehicleOptions?.motorcycles?.feedingTypes?.map((f: any) => ({ value: f.value, label: f.label })) || [];
  const motoStyleOpts    = vehicleOptions?.motorcycles?.styles?.map((s: any) => ({ value: s.value, label: s.label })) || [];
  const motoCategoryOpts = vehicleOptions?.motorcycles?.categories?.map((c: any) => ({ value: c.value, label: c.label })) || [];
  const truckBodyTypeOpts = vehicleOptions?.trucks?.bodyTypes?.map((o: any) => ({ value: o.value, label: o.label })) || [];
  const truckCabTypeOpts  = vehicleOptions?.trucks?.cabTypes?.map((o: any) => ({ value: o.value, label: o.label })) || [];
  const truckTractionOpts = vehicleOptions?.trucks?.tractionTypes?.map((o: any) => ({ value: o.value, label: o.label })) || [];

  // ── Computed values ───────────────────────────────────────────────────────────
  const expensesTotal = formData.expenses?.reduce((s, e) => s + e.value, 0) || 0;
  const alienatedDebt = formData.isAlienated ? formData.alienatedDebt || 0 : 0;
  const netValue = formData.offeredValue - expensesTotal - alienatedDebt;
  const tradeBalance = saleVehiclePrice !== undefined ? netValue - saleVehiclePrice : null;
  const canSave = !!formData.brandId && !!formData.modelId && formData.offeredValue > 0;

  const fipeChips = fipeValue
    ? [
        { label: "FIPE −5%",  pct: 0.95 },
        { label: "FIPE −10%", pct: 0.90 },
        { label: "FIPE −15%", pct: 0.85 },
        { label: "FIPE −20%", pct: 0.80 },
      ]
    : [];

  // ── Helper for Select field ───────────────────────────────────────────────────
  const SelectField = ({
    label,
    required,
    hint,
    value: val,
    onChange: onCh,
    options,
    placeholder,
    disabled,
  }: {
    label: string;
    required?: boolean;
    hint?: string;
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
    placeholder?: string;
    disabled?: boolean;
  }) => (
    <div>
      <FieldLabel required={required} hint={hint}>{label}</FieldLabel>
      <Select value={val} onValueChange={onCh} disabled={disabled}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder || "Selecione..."} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* ── 1. Identificação ───────────────────────────────────────────────── */}
      <SectionCard icon={Tag} title="Identificação do Veículo">
        <div className="space-y-5">

          {/* Categoria */}
          <SelectField
            label="Categoria"
            required
            value={category}
            onChange={(val) => {
              setCategory(val);
              setFormData((p) => ({ ...p, brandId: "", modelId: "", versionId: "" }));
              setBrandSearch(""); setModelSearch(""); setVersionSearch("");
            }}
            options={vehicleOptions?.categories?.map((c: any) => ({ value: c.value, label: c.label })) || []}
            placeholder="Selecione a categoria..."
          />

          {/* Marca / Modelo / Versão */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <FieldLabel required>Marca</FieldLabel>
              <AutocompleteInput
                value={brandSearch}
                options={brandOptions}
                selectedId={formData.brandId}
                onChange={setBrandSearch}
                onSelect={handleBrandSelect}
                placeholder={category ? "Buscar marca..." : "Selecione a categoria primeiro"}
                disabled={!category}
                loading={catalogLoading}
                emptyMessage="Nenhuma marca encontrada"
              />
            </div>
            <div>
              <FieldLabel required>Modelo</FieldLabel>
              <AutocompleteInput
                value={modelSearch}
                options={modelOptions}
                selectedId={formData.modelId}
                onChange={setModelSearch}
                onSelect={handleModelSelect}
                placeholder={formData.brandId ? "Buscar modelo..." : "Selecione uma marca primeiro"}
                disabled={!formData.brandId}
                emptyMessage="Nenhum modelo encontrado"
              />
            </div>
            <div>
              <FieldLabel>Versão</FieldLabel>
              <AutocompleteInput
                value={versionSearch}
                options={versionOptions}
                selectedId={formData.versionId}
                onChange={setVersionSearch}
                onSelect={handleVersionSelect}
                placeholder={formData.modelId ? "Buscar versão..." : "Selecione um modelo primeiro"}
                disabled={!formData.modelId}
                loading={isLoadingVersions}
                emptyMessage="Nenhuma versão encontrada"
              />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── 2. Especificações ──────────────────────────────────────────────── */}
      <SectionCard icon={Settings2} title="Especificações" subtitle="Características técnicas do veículo">
        <div className="space-y-5">

          {/* Linha 1: Condição / Ano Modelo / Ano Fabricação */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <SelectField
              label="Condição"
              required
              value={formData.condition}
              onChange={(v) => set("condition", v)}
              options={conditionOpts}
            />
            <SelectField
              label="Ano Modelo"
              required
              value={formData.modelYear.toString()}
              onChange={(v) => {
                set("modelYear", parseInt(v));
                // Reset ano fabricação ao mudar ano modelo
                set("manufactureYear", parseInt(v));
              }}
              options={modelYearOptions}
              hint={versionYears.length > 0 ? "Anos disponíveis na FIPE para esta versão" : undefined}
            />
            <SelectField
              label="Ano Fabricação"
              required
              value={formData.manufactureYear.toString()}
              onChange={(v) => set("manufactureYear", parseInt(v))}
              options={manufactureYearOptions}
            />
          </div>

          {/* Linha 2: KM / Combustível / Transmissão */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <FieldLabel required>Quilometragem</FieldLabel>
              <Input
                type="number"
                value={formData.mileage.toString()}
                onChange={(e) => set("mileage", parseInt(e.target.value) || 0)}
                placeholder="0"
              />
            </div>
            <SelectField
              label="Combustível"
              required
              value={formData.fuelType}
              onChange={(v) => set("fuelType", v)}
              options={fuelTypeOpts}
              hint={formData.fuelType && formData.versionId ? "Preenchido da versão" : undefined}
            />
            <SelectField
              label="Transmissão"
              required
              value={formData.transmission}
              onChange={(v) => set("transmission", v)}
              options={transmissionOpts}
              disabled={isElectricOrHybrid}
              hint={
                isElectricOrHybrid
                  ? "Automático — padrão elétrico/híbrido"
                  : formData.transmission && formData.versionId
                  ? "Preenchido da versão"
                  : undefined
              }
            />
          </div>

          {/* Campos específicos de CARRO */}
          {isCar && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <SelectField
                label="Tipo de Carroceria"
                value={formData.type || ""}
                onChange={(v) => set("type", v)}
                options={bodyTypeOpts}
                placeholder="Selecione a carroceria..."
              />
              <div>
                <FieldLabel>Número de Portas</FieldLabel>
                <Input
                  type="number"
                  value={formData.doors || ""}
                  onChange={(e) => set("doors", e.target.value)}
                  placeholder="4"
                  min="2"
                  max="5"
                />
              </div>
              <SelectField
                label="Direção"
                value={formData.steering || ""}
                onChange={(v) => set("steering", v)}
                options={steeringOpts}
                hint={formData.steering && formData.versionId ? "Preenchido da versão" : undefined}
              />
            </div>
          )}

          {/* Campos específicos de MOTO */}
          {isMotorcycle && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <SelectField
                label="Tipo de Motor"
                value={formData.motorcycleEngineType || ""}
                onChange={(v) => set("motorcycleEngineType", v)}
                options={motoEngineOpts}
                placeholder="Tipo de motor..."
              />
              <SelectField
                label="Resfriamento"
                value={formData.motorcycleCoolingType || ""}
                onChange={(v) => set("motorcycleCoolingType", v)}
                options={motoCoolingOpts}
                placeholder="Tipo de resfriamento..."
              />
              <SelectField
                label="Partida"
                value={formData.motorcycleStarterType || ""}
                onChange={(v) => set("motorcycleStarterType", v)}
                options={motoStarterOpts}
                placeholder="Tipo de partida..."
              />
              <SelectField
                label="Freio Dianteiro"
                value={formData.motorcycleBrakeType || ""}
                onChange={(v) => set("motorcycleBrakeType", v)}
                options={motoBrakeOpts}
                placeholder="Tipo de freio..."
              />
              <SelectField
                label="Alimentação"
                value={formData.motorcycleFeedingType || ""}
                onChange={(v) => set("motorcycleFeedingType", v)}
                options={motoFeedingOpts}
                placeholder="Tipo de alimentação..."
              />
              <SelectField
                label="Estilo"
                value={formData.motorcycleStyle || ""}
                onChange={(v) => set("motorcycleStyle", v)}
                options={motoStyleOpts}
                placeholder="Estilo da moto..."
              />
              <SelectField
                label="Categoria"
                value={formData.motorcycleCategory || ""}
                onChange={(v) => set("motorcycleCategory", v)}
                options={motoCategoryOpts}
                placeholder="Categoria da moto..."
              />
              <div>
                <FieldLabel>Cilindrada (cc)</FieldLabel>
                <Input
                  type="number"
                  value={formData.motorcycleCylinderCapacity || ""}
                  onChange={(e) => set("motorcycleCylinderCapacity", e.target.value)}
                  placeholder="150"
                  min="0"
                />
              </div>
            </div>
          )}

          {/* Campos específicos de CAMINHÃO */}
          {isTruck && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <SelectField
                label="Tipo de Carroceria"
                value={formData.truckBodyType || ""}
                onChange={(v) => set("truckBodyType", v)}
                options={truckBodyTypeOpts}
                placeholder="Tipo de carroceria..."
              />
              <SelectField
                label="Tipo de Cabine"
                value={formData.truckCabType || ""}
                onChange={(v) => set("truckCabType", v)}
                options={truckCabTypeOpts}
                placeholder="Tipo de cabine..."
              />
              <SelectField
                label="Tração"
                value={formData.truckTraction || ""}
                onChange={(v) => set("truckTraction", v)}
                options={truckTractionOpts}
                placeholder="Tração..."
              />
              <div>
                <FieldLabel>Número de Eixos</FieldLabel>
                <Input
                  type="number"
                  value={formData.truckAxles || ""}
                  onChange={(e) => set("truckAxles", e.target.value)}
                  placeholder="3"
                  min="2"
                  max="10"
                />
              </div>
              <div>
                <FieldLabel>PBT — Peso Bruto Total (kg)</FieldLabel>
                <Input
                  type="number"
                  value={formData.truckGrossVehicleWeight || ""}
                  onChange={(e) => set("truckGrossVehicleWeight", e.target.value)}
                  placeholder="23000"
                  min="0"
                />
              </div>
              <div>
                <FieldLabel>Capacidade de Carga (kg)</FieldLabel>
                <Input
                  type="number"
                  value={formData.truckPayload || ""}
                  onChange={(e) => set("truckPayload", e.target.value)}
                  placeholder="15000"
                  min="0"
                />
              </div>
            </div>
          )}

          {/* Cor */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <SelectField
              label="Cor"
              required
              value={formData.color}
              onChange={(v) => set("color", v)}
              options={colorOpts}
            />
          </div>
        </div>
      </SectionCard>

      {/* ── 3. Documentação (colapsável) ───────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card">
        <button
          type="button"
          onClick={() => setShowDocFields((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-surface transition-colors rounded-xl"
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-surface shrink-0">
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <span className="text-sm font-semibold text-foreground">Documentação</span>
              <span className="ml-2 text-xs text-muted-foreground">(opcional)</span>
            </div>
          </div>
          {showDocFields
            ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          }
        </button>

        {showDocFields && (
          <div className="px-6 pb-6 pt-1 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-4">
              <div>
                <FieldLabel htmlFor="plate">Placa</FieldLabel>
                <Input
                  id="plate"
                  value={formData.plate || ""}
                  onChange={(e) => set("plate", e.target.value.toUpperCase())}
                  placeholder="ABC1D23"
                  maxLength={7}
                />
              </div>
              <div>
                <FieldLabel htmlFor="chassis">Chassi</FieldLabel>
                <Input
                  id="chassis"
                  value={formData.chassis || ""}
                  onChange={(e) => set("chassis", e.target.value.toUpperCase())}
                  placeholder="9BW12345678901234"
                  maxLength={17}
                />
              </div>
              <div>
                <FieldLabel htmlFor="renavam">RENAVAM</FieldLabel>
                <Input
                  id="renavam"
                  value={formData.renavam || ""}
                  onChange={(e) => set("renavam", e.target.value)}
                  placeholder="12345678901"
                  maxLength={11}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 4. Avaliação ───────────────────────────────────────────────────── */}
      <SectionCard icon={DollarSign} title="Avaliação" subtitle="Valor de troca e referência FIPE">
        <div className="space-y-6">

          {/* Valor Oferecido */}
          <div>
            <FieldLabel required>Valor Oferecido pela Troca</FieldLabel>
            <PriceInput
              value={formData.offeredValue > 0 ? formatCurrency(formData.offeredValue) : ""}
              onChange={(fmt) => set("offeredValue", parseCurrency(fmt))}
              placeholder="R$ 0,00"
            />
          </div>

          {/* FIPE box */}
          {(isLoadingFipe || fipeValue || (!fipeValue && !isLoadingFipe && formData.versionId && formData.modelYear && versionYears.length > 0)) && (
            <div className="rounded-lg border border-border bg-surface p-5 space-y-4">
              {isLoadingFipe && (
                <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Consultando tabela FIPE...
                </div>
              )}
              {fipeValue && !isLoadingFipe && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Referência FIPE
                    </span>
                    <span className="text-lg font-bold text-foreground tabular-nums">
                      {fipeValue.value}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Usar como valor ofertado:</p>
                    <div className="flex flex-wrap gap-2">
                      {fipeChips.map((chip) => {
                        const chipVal = Math.round(fipeValue.numericValue * chip.pct);
                        const isSelected = formData.offeredValue === chipVal;
                        return (
                          <button
                            key={chip.label}
                            type="button"
                            onClick={() => set("offeredValue", chipVal)}
                            className={cn(
                              "px-3 py-1.5 rounded-md text-xs font-semibold border transition-all",
                              isSelected
                                ? "bg-accent text-white border-accent shadow-sm"
                                : "bg-card border-border text-muted-foreground hover:border-accent/60 hover:text-foreground"
                            )}
                          >
                            {chip.label}
                            <span className="ml-1.5 font-mono font-normal opacity-80">
                              {formatCurrency(chipVal)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              {!fipeValue && !isLoadingFipe && formData.versionId && formData.modelYear && versionYears.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Valor FIPE não encontrado para este ano/versão.
                </p>
              )}
            </div>
          )}

          {/* Painel de Desvalorização */}
          {fipeValue && !isLoadingFipe && (
            <DepreciationPanel
              fipeValue={fipeValue.numericValue}
              mileage={formData.mileage}
              modelYear={formData.modelYear}
              condition={formData.condition}
              onApplySuggested={(val) => set("offeredValue", val)}
            />
          )}

          {/* Alienação */}
          <div className="rounded-xl border border-border">
            <div className="flex items-center justify-between gap-4 px-5 py-4">
              <div className="flex items-center gap-3 min-w-0">
                <Banknote className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground leading-none">
                    Veículo Alienado / Com Financiamento
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Existe financiamento, consórcio ou ônus sobre este veículo?
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData.isAlienated}
                onClick={() => set("isAlienated", !formData.isAlienated)}
                className={cn(
                  "relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200",
                  formData.isAlienated ? "bg-accent" : "bg-border"
                )}
              >
                <span className={cn(
                  "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200",
                  formData.isAlienated ? "translate-x-4" : "translate-x-0"
                )} />
              </button>
            </div>
            {formData.isAlienated && (
              <div className="px-5 py-5 space-y-4 border-t border-border bg-surface rounded-b-xl">
                <div>
                  <FieldLabel required>Valor da Dívida Restante</FieldLabel>
                  <PriceInput
                    value={formData.alienatedDebt && formData.alienatedDebt > 0 ? formatCurrency(formData.alienatedDebt) : ""}
                    onChange={(fmt) => set("alienatedDebt", parseCurrency(fmt))}
                    placeholder="R$ 0,00"
                  />
                </div>
                <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    O valor da dívida será <strong>descontado do valor líquido</strong> do veículo no resumo da proposta.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Despesas */}
          <div className="space-y-4">
            <div className="flex items-center gap-2.5 pb-2 border-b border-border">
              <TrendingDown className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Despesas sobre o Veículo
              </span>
            </div>

            <div className="flex gap-2.5">
              <Input
                value={expenseName}
                onChange={(e) => setExpenseName(e.target.value)}
                placeholder="Ex: Revisão, pintura, funilaria..."
                className="flex-1"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddExpense(); } }}
              />
              <div className="w-36 shrink-0">
                <PriceInput value={expenseValue} onChange={setExpenseValue} placeholder="R$ 0,00" />
              </div>
              <Button type="button" variant="outline" onClick={handleAddExpense} disabled={!expenseName || !expenseValue} title="Adicionar despesa">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {formData.expenses && formData.expenses.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                {formData.expenses.map((expense, idx) => (
                  <div
                    key={idx}
                    className={cn("flex items-center justify-between px-4 py-3", idx < formData.expenses!.length - 1 && "border-b border-border")}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm text-foreground truncate">{expense.name}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold font-mono text-destructive">
                        − {formatCurrency(expense.value)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveExpense(idx)}
                        className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 py-3 bg-surface border-t border-border">
                  <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Total de despesas</span>
                  <span className="text-sm font-bold font-mono text-destructive">− {formatCurrency(expensesTotal)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* ── 5. Resumo Financeiro ───────────────────────────────────────────── */}
      {formData.offeredValue > 0 && (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="px-6 py-4 bg-surface border-b border-border">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Resumo da Avaliação
            </span>
          </div>
          <div className="px-6 py-5 space-y-3 bg-card">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Valor ofertado</span>
              <span className="font-semibold font-mono text-foreground">{formatCurrency(formData.offeredValue)}</span>
            </div>
            {formData.expenses?.map((expense, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground pl-4 flex items-center gap-1.5">
                  <span className="opacity-50">↳</span>{expense.name}
                </span>
                <span className="font-mono text-destructive">− {formatCurrency(expense.value)}</span>
              </div>
            ))}
            {formData.isAlienated && alienatedDebt > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground pl-4 flex items-center gap-1.5">
                  <span className="opacity-50">↳</span>
                  <Banknote className="h-3.5 w-3.5" /> Dívida (alienação)
                </span>
                <span className="font-mono text-destructive">− {formatCurrency(alienatedDebt)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <span className="text-sm font-bold text-foreground">Valor Líquido da Troca</span>
              <span className="text-base font-bold font-mono text-accent">{formatCurrency(netValue)}</span>
            </div>
          </div>

          {/* Saldo da troca */}
          {tradeBalance !== null && (
            <div className={cn("px-6 py-5 border-t border-border", tradeBalance > 0 ? "bg-emerald-50" : "bg-surface")}>
              {saleVehiclePrice !== undefined && (
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-muted-foreground">Preço do veículo em venda</span>
                  <span className="font-mono text-muted-foreground">{formatCurrency(saleVehiclePrice)}</span>
                </div>
              )}
              {tradeBalance > 0 ? (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-100 border border-emerald-300">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-600 shrink-0 mt-0.5">
                    <TrendingUp className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-emerald-800">A loja deve pagar ao cliente</p>
                    <p className="text-xs text-emerald-700 mt-0.5">O valor da troca supera o preço do veículo em venda</p>
                    <p className="text-2xl font-bold font-mono text-emerald-700 mt-2">{formatCurrency(tradeBalance)}</p>
                  </div>
                </div>
              ) : tradeBalance < 0 ? (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent shrink-0 mt-0.5">
                    <ArrowLeftRight className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">Saldo a pagar pelo cliente</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Após abatimento da troca no valor do veículo</p>
                    <p className="text-2xl font-bold font-mono text-accent mt-2">{formatCurrency(Math.abs(tradeBalance))}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-border">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/10 shrink-0">
                    <ArrowLeftRight className="h-4 w-4 text-accent" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Troca exata — sem saldo adicional</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Botão Salvar ───────────────────────────────────────────────────── */}
      {showSaveButton && (
        <div className="pt-2">
          <Button type="button" onClick={() => onSave && onSave(formData)} disabled={!canSave} className="w-full">
            Confirmar Veículo de Troca
          </Button>
        </div>
      )}
    </div>
  );
};
